import { fromUnixTime } from 'date-fns';
import { getManyFromList } from '@soundxyz/utils';
import type {
  AuctionConfigurationPriceLimits,
  ManuallyAddedAllowlist,
  ManuallyAddedAllowlistKeys,
} from '../fields';
import type { EditAuctionConfigurationErrorsType } from './errors';
import { initialEditAuctionConfigurationErrors } from './errors';
import type { AllowlistsSchema } from './fields/allowlist';
import type { EditedAuctionConfigSchema, EditedSaleScheduleToUpdate } from './fields/auctionConfig';
import { editedAuctionConfigState } from './fields/auctionConfig';
import type {
  EditCollectorsOfArtists,
  EditCollectorsOfArtistsKeys,
  EditCollectorsOfReleases,
  EditCollectorsOfReleasesKeys,
} from './fields/releaseInfo';
import { validateEditedAuctionConfigState } from './validation';

/**
 * Function that takes an array of schedules and returns an object with one Public Sale and the first Presale if exists
 */
export function parseMintSchedules({
  schedules,
  errors,
  priceLimits,
}: {
  schedules: EditedSaleScheduleToUpdate[] | undefined;
  errors: EditAuctionConfigurationErrorsType;
  priceLimits: AuctionConfigurationPriceLimits;
}) {
  const { presaleMaxPrice, presaleMinPrice, publicMaxPrice, publicMinPrice } = priceLimits;

  if (!schedules?.length) {
    return null;
  }

  const customSchedules = schedules.map(schedule => {
    const isMerkle = schedule.mintType === 'MerkleDrop';

    if (isMerkle) {
      return {
        ...schedule,
        startTime: fromUnixTime(schedule.startTimeSeconds),
        endTime: fromUnixTime(schedule.endTimeSeconds),
        minPrice: presaleMinPrice,
        maxPrice: presaleMaxPrice,
        errors: {
          startTime: errors.presaleStartTime,
          endTime: errors.presaleEndTime,
          price: errors.presalePricePerMint,
          maxMintable: errors.presaleMaxSupply,
          maxMintablePerAccount: errors.presaleMaxMintsPerWallet,
        },
      };
    }

    return {
      ...schedule,
      startTime: fromUnixTime(schedule.startTimeSeconds),
      cutoffTime: fromUnixTime(schedule.cutoffTimeSeconds),
      minPrice: publicMinPrice,
      maxPrice: publicMaxPrice,
      errors: {
        startTime: errors.publicStartTime,
        endTime: errors.publicEndTime,
        price: errors.publicPricePerMint,
        maxMintable: errors.maxMintable,
        maxMintablePerAccount: errors.publicMaxMintsPerWallet,
      },
    };
  });

  const rangeSchedules = getManyFromList(customSchedules, s =>
    s.mintType === 'RangeEdition' ? s : null,
  );

  const merkleSchedules = getManyFromList(customSchedules, s =>
    s.mintType === 'MerkleDrop' ? s : null,
  );

  return {
    rangeSchedules,
    merkleSchedules,
  };
}

export const clearAuctionConfigErrors = () => {
  editedAuctionConfigState.errors = initialEditAuctionConfigurationErrors();
};

export const editManuallyAddedAllowlistFields = <Key extends ManuallyAddedAllowlistKeys>({
  name,
  value,
  releaseId,
  userChange,
}: {
  name: Key;
  value: ManuallyAddedAllowlist[Key];
  releaseId: string;
  userChange: boolean;
}) => {
  const allowlists = editedAuctionConfigState.fields.allowlists[releaseId];

  if (!allowlists) {
    editedAuctionConfigState.fields.allowlists[releaseId] = [
      {
        collectorsOfReleases: null,
        collectorsOfArtists: null,
        manuallyAddedAllowlist: {
          description: null,
          hasAddresses: false,
          addresses: null,
        },
        userChange,
      },
    ];
  }

  if (allowlists && allowlists[0]) {
    allowlists[0].userChange ||= userChange;

    if (!allowlists[0].manuallyAddedAllowlist) {
      allowlists[0].manuallyAddedAllowlist = {
        description: null,
        hasAddresses: null,
        addresses: null,
      };
    }

    allowlists[0].manuallyAddedAllowlist[name] = value;
  }
};

export const editCollectorsOfReleasesFields = <Key extends EditCollectorsOfReleasesKeys>({
  name,
  value,
  releaseId,
  userChange,
}: {
  name: Key;
  value: EditCollectorsOfReleases[Key];
  releaseId: string;
  userChange: boolean;
}) => {
  const allowlists = editedAuctionConfigState.fields.allowlists[releaseId];
  if (!allowlists) {
    editedAuctionConfigState.fields.allowlists[releaseId] = [
      {
        collectorsOfReleases: null,
        collectorsOfArtists: null,
        manuallyAddedAllowlist: {
          description: null,
          hasAddresses: false,
          addresses: null,
        },
        userChange,
      },
    ];
  }

  if (allowlists && allowlists[0]) {
    allowlists[0].userChange ||= userChange;

    if (!allowlists[0].collectorsOfReleases) {
      allowlists[0].collectorsOfReleases = {
        selectedReleaseIds: [],
        allArtistCollaborations: false,
        allArtistReleases: false,
        releasesInfo: [],
      };
    }

    allowlists[0].collectorsOfReleases[name] = value;
  }
};

export const editCollectorsOfArtistsFields = <Key extends EditCollectorsOfArtistsKeys>({
  name,
  value,
  releaseId,
  userChange,
}: {
  name: Key;
  value: EditCollectorsOfArtists[Key];
  releaseId: string;
  userChange: boolean;
}) => {
  const allowlists = editedAuctionConfigState.fields.allowlists[releaseId];

  if (!allowlists) {
    editedAuctionConfigState.fields.allowlists[releaseId] = [
      {
        collectorsOfReleases: null,
        collectorsOfArtists: null,
        manuallyAddedAllowlist: {
          description: null,
          hasAddresses: false,
          addresses: null,
        },
        userChange,
      },
    ];
  }

  if (allowlists && allowlists[0]) {
    allowlists[0].userChange ||= userChange;

    if (!allowlists[0].collectorsOfArtists) {
      allowlists[0].collectorsOfArtists = {
        allArtistCollectors: true,
        selectedArtistIds: [],
        allCollaboratingArtists: false,
        artistsInfo: [],
      };
    }

    allowlists[0].collectorsOfArtists[name] = value;
  }
};

type Allowlist = (typeof AllowlistsSchema)['_input'][number];

export const editAllowlistFields = <Key extends keyof Allowlist[0]>(
  name: Key,
  value: Allowlist[0][Key],
  releaseId: string,
) => {
  const allowlists = editedAuctionConfigState.fields.allowlists[releaseId];

  if (!allowlists) {
    editedAuctionConfigState.fields.allowlists[releaseId] = [
      {
        collectorsOfReleases: null,
        collectorsOfArtists: null,
        manuallyAddedAllowlist: {
          description: null,
          hasAddresses: false,
          addresses: null,
        },
        userChange: false,
      },
    ];
  }

  if (allowlists && allowlists[0]) {
    allowlists[0][name] = value;
  }
};

export const mapPersistedAllowlistsToFormValues = ({
  releaseId,
  persistedAllowlists,
}: {
  releaseId: string;
  persistedAllowlists: (typeof EditedAuctionConfigSchema)['_input']['fields']['allowlists']['releaseId'];
}) => {
  if (editedAuctionConfigState.fields.allowlists) {
    editedAuctionConfigState.fields.allowlists[releaseId] = persistedAllowlists;
  }

  validateEditedAuctionConfigState();
};
